// frontend/src/components/EngagementModal.js
import React, { useEffect, useState } from "react";
import { api } from "api";
import { Link } from "react-router-dom";
import { CheckCircle, X } from "phosphor-react";
import { DEFAULT_CIRCLE_IMAGE, DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

const Row = ({ user, canConnect, isConnected, onConnect }) => (
  <div className="flex items-center justify-between py-2">
    {/* LEFT SIDE: avatar + name clickable */}
    <Link
      to={`/profile/${user?.id}`}
      className="flex items-center gap-3"
    >
      <img
        src={user?.profile_pic_url || DEFAULT_PROFILE_IMAGE}
        alt={user?.name || "user"}
        className="w-7 h-7 rounded-full object-cover"
        loading="lazy"
        decoding="async"
      />
      <div className="text-sm">
        <div className="font-medium text-gray-900 hover:font-bold hover:text-blue-600">
          {user?.name || "User"}
        </div>
      </div>
    </Link>

    {/* RIGHT SIDE: button or Connected pill */}
    {canConnect ? (
      <button
        onClick={() => onConnect?.(user)}
        type="button"
        className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-400"
      >
        Connect
      </button>
    ) : isConnected ? (
      <span className="text-xs text-blue-400 px-3 flex items-center gap-1">
        <CheckCircle size={14} weight="bold" /> Connected
      </span>
    ) : null}
  </div>
);

const EngagementModal = ({
  activityId,
  isOpen,
  onClose,
  defaultTab = "highfives",
  currentUser,
}) => {
  const [tab, setTab] = useState(defaultTab); // "highfives" | "comments"
  const [loading, setLoading] = useState(false);
  const [highfivers, setHighfivers] = useState([]);
  const [commenters, setCommenters] = useState([]);
  const [error, setError] = useState("");

  // who the viewer is already connected to
  const [connectedIds, setConnectedIds] = useState([]);

  useEffect(() => setTab(defaultTab), [defaultTab]);

  // Load engagement lists
  useEffect(() => {
    if (!isOpen) return;
    const fetchLists = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const res = await api.get(`/activities/${activityId}/engagement`, { headers });
        setHighfivers(res.data?.highfivers || []);
        setCommenters(res.data?.commenters || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load engagement.");
      } finally {
        setLoading(false);
      }
    };
    fetchLists();
  }, [isOpen, activityId]);

  // Load who I'm already connected to
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchConnections = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };

        const meId = currentUser?.id || localStorage.getItem("user_id");
        const url = meId
          ? `/connections/following/${meId}`
          : "/connections/following/me";

        const { data } = await api.get(url, { headers });
        const list = Array.isArray(data) ? data : [];
        const ids = list
          .map((u) => u?.id)
          .filter((id) => typeof id === "number" || typeof id === "string");

        if (!cancelled) {
          setConnectedIds(ids);
        }
      } catch {
        // non-fatal
      }
    };

    fetchConnections();
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentUser]);

  const handleConnect = async (user) => {
    try {
      const token = localStorage.getItem("token");
      await api.post(
        "/connections/request",
        { addressee_id: user.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Optimistically mark as not connectable in both lists
      setHighfivers((list) =>
        list.map((u) =>
          u.id === user.id ? { ...u, can_connect: false } : u
        )
      );
      setCommenters((list) =>
        list.map((u) =>
          u.id === user.id ? { ...u, can_connect: false } : u
        )
      );

      // Mark as connected/pending locally so button changes to "Connected"
      setConnectedIds((prev) =>
        prev.includes(user.id) ? prev : [...prev, user.id]
      );
    } catch (e) {
      console.error(e?.response?.data || e.message);
    }
  };

  if (!isOpen) return null;

  const computeCanConnect = (u) => {
    if (!u || !u.id) return false;
    if (currentUser?.id && u.id === currentUser.id) return false; // no connect for self

    const isConnected = connectedIds.includes(u.id);
    const backendAllows =
      typeof u.can_connect === "boolean" ? u.can_connect : true;

    return backendAllows && !isConnected;
  };

  const isConnected = (u) => {
    if (!u || !u.id) return false;
    if (currentUser?.id && u.id === currentUser.id) return false; // don't show "Connected" for self
    return connectedIds.includes(u.id);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div
        className="relative bg-white rounded-2xl shadow-[0_8px_0px_rgba(37,99,235,0.96)] w-full max-w-[350px] px-5 py-5 pt-4 pb-3"
        style={{ maxHeight: "90svh" }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition"
          aria-label="Close"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="flex gap-4 mt-3">
          <button
            type="button"
            className={`px-3 !min-h-0 !h-8 rounded-full text-sm font-medium ${
              tab === "highfives" ? "bg-blue-100 font-semibold" : "bg-gray-100 hover:bg-blue-100"
            }`}
            onClick={() => setTab("highfives")}
          >
            High Fives
          </button>
          <button
            type="button"
            className={`px-3 !min-h-0 !h-8 text-sm rounded-full font-medium ${
              tab === "comments" ? "bg-blue-100 font-semibold" : "bg-gray-100 hover:bg-blue-100"
            }`}
            onClick={() => setTab("comments")}
          >
            Comments
          </button>
        </div>

        {error && (
          <div className="mt-3 text-sm text-red-600">{error}</div>
        )}

        <div
          className="mt-2 px-2 text-sm max-h-80 overflow-auto"
          style={{
            WebkitOverflowScrolling: "touch",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          {loading ? (
            <div className="text-sm text-gray-500 p-2">Loading…</div>
          ) : tab === "highfives" ? (
            highfivers.length === 0 ? (
              <div className="text-sm text-gray-500 p-2">
                No high fives yet
              </div>
            ) : (
              highfivers.map((u) => (
                <Row
                  key={u.id}
                  user={u}
                  canConnect={computeCanConnect(u)}
                  isConnected={isConnected(u)}
                  onConnect={handleConnect}
                />
              ))
            )
          ) : commenters.length === 0 ? (
            <div className="text-sm text-gray-500 p-2">
              No comments yet
            </div>
          ) : (
            commenters.map((u) => (
              <Row
                key={u.id}
                user={u}
                canConnect={computeCanConnect(u)}
                isConnected={isConnected(u)}
                onConnect={handleConnect}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EngagementModal;
